import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  SafeAreaView,
  ScrollView,
} from 'react-native';
import { ms, s, vs } from '../utils/responsive';
import errorLoggingService from '../services/errorLoggingService';

import { createLogger } from '../utils/logger';
const logger = createLogger('ErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: any[];
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error reporting service
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Log to error logging service
    errorLoggingService.logError(error, errorInfo, {
      boundaryName: 'ErrorBoundary',
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error boundary when specified props change
    if (this.props.resetOnPropsChange) {
      const hasChanged = this.props.resetOnPropsChange.some(
        (prop, index) => prop !== prevProps.resetOnPropsChange?.[index]
      );

      if (hasChanged && this.state.hasError) {
        this.resetError();
      }
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <SafeAreaView style={styles.container}>
          <ScrollView
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.iconContainer}>
              <Text style={styles.errorIcon}>⚠️</Text>
            </View>

            <Text style={styles.title}>Oops! Something went wrong</Text>

            <Text style={styles.description}>
              We're sorry for the inconvenience. An unexpected error occurred.
              Please try again.
            </Text>

            <TouchableOpacity
              style={styles.primaryButton}
              onPress={this.resetError}
            >
              <Text style={styles.primaryButtonText}>Try Again</Text>
            </TouchableOpacity>

            {__DEV__ && this.state.error && (
              <View style={styles.errorDetailsContainer}>
                <Text style={styles.errorDetailsTitle}>
                  Error Details (Development Only):
                </Text>
                <View style={styles.errorDetailsBox}>
                  <Text style={styles.errorDetailsText}>
                    {this.state.error.toString()}
                  </Text>
                  {this.state.errorInfo && (
                    <Text style={styles.errorDetailsText}>
                      {this.state.errorInfo.componentStack}
                    </Text>
                  )}
                </View>
              </View>
            )}
          </ScrollView>
        </SafeAreaView>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(30),
    paddingVertical: ms(40),
  },
  iconContainer: {
    marginBottom: ms(30),
  },
  errorIcon: {
    fontSize: ms(80, 0.3),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: ms(15),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
    marginBottom: ms(40),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(15),
    paddingHorizontal: ms(40),
    borderRadius: ms(25),
    minWidth: s(200),
    alignItems: 'center',
    marginBottom: ms(20),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  errorDetailsContainer: {
    width: '100%',
    marginTop: ms(30),
  },
  errorDetailsTitle: {
    fontSize: ms(14, 0.3),
    fontWeight: 'bold',
    color: '#E85C5C',
    marginBottom: ms(10),
  },
  errorDetailsBox: {
    backgroundColor: '#FFE5E5',
    borderRadius: ms(10),
    padding: ms(15),
    borderWidth: ms(1),
    borderColor: '#E85C5C',
  },
  errorDetailsText: {
    fontSize: ms(12, 0.3),
    color: '#333',
    fontFamily: 'monospace',
    lineHeight: ms(18, 0.3),
  },
});

export default ErrorBoundary;
